import { useState } from 'react'
import { ShoppingCart, Download, Copy, CheckCircle, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select, SelectOption } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Recipe } from '@/types/recipe'
import { shoppingListService, type ShoppingListExportOptions, type ShoppingListItem } from '@/services/ingredients'

interface ShoppingListExportProps {
  recipe: Recipe
  className?: string
  variant?: 'button' | 'card'
}

export function ShoppingListExport({ recipe, className, variant = 'button' }: ShoppingListExportProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [servings, setServings] = useState(recipe.servings || 4)
  const [selectedFormat, setSelectedFormat] = useState<'text' | 'csv' | 'json'>('text')
  const [isExporting, setIsExporting] = useState(false)
  const [exportResult, setExportResult] = useState<{ success: boolean; message: string } | null>(null)
  const [previewItems, setPreviewItems] = useState<ShoppingListItem[]>([])

  const handleExport = async () => {
    setIsExporting(true)
    setExportResult(null)

    try {
      const options: ShoppingListExportOptions = {
        format: selectedFormat,
        servings: servings,
        includeTitle: true,
        includeInstructions: false
      }

      const result = await shoppingListService.exportToFormat(recipe, options)

      if (result.success) {
        setPreviewItems(result.items || [])

        // Handle file downloads
        const timestamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-')
        const filename = `${recipe.title.replace(/[^a-zA-Z0-9]/g, '-')}-${timestamp}`

        if (selectedFormat === 'csv') {
          shoppingListService.downloadAsFile(result.data as string, `${filename}.csv`, 'text/csv')
        } else if (selectedFormat === 'json') {
          shoppingListService.downloadAsFile(result.data as string, `${filename}.json`, 'application/json')
        } else {
          // Text format - copy to clipboard and offer download
          const copied = await shoppingListService.copyToClipboard(result.data as string)
          if (copied) {
            setExportResult({
              success: true,
              message: 'Boodschappenlijst gekopieerd naar klembord!'
            })
          } else {
            shoppingListService.downloadAsFile(result.data as string, `${filename}.txt`, 'text/plain')
            setExportResult({
              success: true,
              message: 'Boodschappenlijst gedownload als tekstbestand!'
            })
          }
        }

        if (selectedFormat !== 'text') {
          setExportResult({
            success: true,
            message: `Boodschappenlijst geëxporteerd als ${selectedFormat.toUpperCase()} bestand!`
          })
        }
      } else {
        setExportResult({
          success: false,
          message: result.error || 'Er is een fout opgetreden bij het exporteren'
        })
      }
    } catch (error) {
      setExportResult({
        success: false,
        message: 'Er is een onverwachte fout opgetreden'
      })
    } finally {
      setIsExporting(false)
    }
  }

  const generatePreview = () => {
    const items = shoppingListService.convertRecipeToShoppingList(recipe, { servings })
    setPreviewItems(items)
  }

  // Generate preview when component opens or servings change
  const handleOpen = () => {
    setIsOpen(true)
    generatePreview()
  }

  if (variant === 'button') {
    return (
      <>
        <Button
          variant="outline"
          onClick={handleOpen}
          className={className}
          size="sm"
        >
          <ShoppingCart className="h-4 w-4 mr-2" />
          Boodschappenlijst
        </Button>

        {isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center">
                    <ShoppingCart className="h-5 w-5 mr-2" />
                    Boodschappenlijst maken
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsOpen(false)}
                  >
                    ✕
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <ShoppingListExportContent
                  recipe={recipe}
                  servings={servings}
                  setServings={setServings}
                  selectedFormat={selectedFormat}
                  setSelectedFormat={setSelectedFormat}
                  previewItems={previewItems}
                  isExporting={isExporting}
                  exportResult={exportResult}
                  onExport={handleExport}
                  onPreviewUpdate={generatePreview}
                />
              </CardContent>
            </Card>
          </div>
        )}
      </>
    )
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center">
          <ShoppingCart className="h-5 w-5 mr-2" />
          Boodschappenlijst maken
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <ShoppingListExportContent
          recipe={recipe}
          servings={servings}
          setServings={setServings}
          selectedFormat={selectedFormat}
          setSelectedFormat={setSelectedFormat}
          previewItems={previewItems}
          isExporting={isExporting}
          exportResult={exportResult}
          onExport={handleExport}
          onPreviewUpdate={generatePreview}
        />
      </CardContent>
    </Card>
  )
}

interface ShoppingListExportContentProps {
  recipe: Recipe
  servings: number
  setServings: (servings: number) => void
  selectedFormat: 'text' | 'csv' | 'json'
  setSelectedFormat: (format: 'text' | 'csv' | 'json') => void
  previewItems: ShoppingListItem[]
  isExporting: boolean
  exportResult: { success: boolean; message: string } | null
  onExport: () => void
  onPreviewUpdate: () => void
}

const isExportFormat = (value: string): value is 'text' | 'csv' | 'json' =>
  value === 'text' || value === 'csv' || value === 'json'

function ShoppingListExportContent({
  recipe,
  servings,
  setServings,
  selectedFormat,
  setSelectedFormat,
  previewItems,
  isExporting,
  exportResult,
  onExport,
  onPreviewUpdate
}: ShoppingListExportContentProps) {

  return (
    <>
      {/* Settings */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Aantal porties
          </label>
          <div className="flex items-center space-x-2">
            <Input
              type="number"
              min="1"
              max="20"
              value={servings}
              onChange={(e) => {
                const newServings = parseInt(e.target.value) || 1
                setServings(newServings)
                onPreviewUpdate()
              }}
              className="w-20"
            />
            {recipe.servings && servings !== recipe.servings && (
              <Badge variant="outline">
                Aangepast van {recipe.servings} porties
              </Badge>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Export formaat
          </label>
          <Select
            value={selectedFormat}
            onChange={(e) => {
              if (isExportFormat(e.target.value)) {
                setSelectedFormat(e.target.value)
              }
            }}
          >
            <SelectOption value="text">📝 Tekst (klembord)</SelectOption>
            <SelectOption value="csv">📊 CSV bestand</SelectOption>
            <SelectOption value="json">🔧 JSON bestand</SelectOption>
          </Select>
        </div>
      </div>

      {/* Preview */}
      <div>
        <h4 className="font-medium mb-3">Voorvertoning ({previewItems.length} items)</h4>
        <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
          {previewItems.length > 0 ? (
            <ul className="space-y-2">
              {previewItems.map((item, index) => (
                <li key={index} className="flex items-center text-sm">
                  <span className="w-2 h-2 bg-orange-500 rounded-full mr-3 flex-shrink-0"></span>
                  <span className="flex-1">
                    <span className="font-medium">{item.name}</span>
                    {(item.quantity || item.unit || item.specification) && (
                      <span className="text-gray-600 ml-2">
                        {item.quantity && item.unit && `(${item.quantity} ${item.unit})`}
                        {item.quantity && !item.unit && `(${item.quantity}x)`}
                        {item.specification && ` - ${item.specification}`}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 text-sm">Geen ingrediënten gevonden</p>
          )}
        </div>
      </div>

      {/* Export Result */}
      {exportResult && (
        <div className={`p-3 rounded-lg flex items-center ${
          exportResult.success 
            ? 'bg-green-50 border border-green-200 text-green-800'
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {exportResult.success ? (
            <CheckCircle className="h-4 w-4 mr-2" />
          ) : (
            <AlertCircle className="h-4 w-4 mr-2" />
          )}
          <span className="text-sm">{exportResult.message}</span>
        </div>
      )}

      {/* Export Button */}
      <div className="flex gap-3">
        <Button
          onClick={onExport}
          disabled={isExporting || previewItems.length === 0}
          className="flex-1"
        >
          {isExporting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Exporteren...
            </>
          ) : (
            <>
              {selectedFormat === 'text' ? (
                <Copy className="h-4 w-4 mr-2" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {selectedFormat === 'text' && 'Kopieer naar klembord'}
              {selectedFormat === 'csv' && 'Download CSV'}
              {selectedFormat === 'json' && 'Download JSON'}
            </>
          )}
        </Button>
      </div>
    </>
  )
}
